import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";
import { LightningBolt } from "@/components/LightningBolt";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [domain, setDomain] = useState("local");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Signed in.");
      navigate("/admin/leads", { replace: true });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO title="Sign in to LightningFlow · Management Server" description="LightningFlow internal management server sign-in." canonicalPath="/admin/login" noIndex />

      <div
        className="min-h-screen relative overflow-hidden flex flex-col"
        style={{
          background:
            "radial-gradient(900px 600px at 50% -10%, rgba(56,189,248,0.18), transparent 60%)," +
            "radial-gradient(700px 500px at 80% 110%, rgba(59,130,246,0.18), transparent 65%)," +
            "linear-gradient(180deg, #020617 0%, #0B1220 60%, #0B1220 100%)",
        }}
        data-testid="admin-login-page"
      >
        {/* Subtle grid backdrop */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(186,230,253,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(186,230,253,0.5) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        {/* Mini top bar (transparent) */}
        <header className="relative z-10">
          <div className="lf-container flex h-20 items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5 text-white" data-testid="admin-login-home-link">
              <LightningBolt size={48} />
              <span className="font-display text-base font-extrabold tracking-tight">LightningFlow</span>
            </Link>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-sky-200/70">
              Internal · Restricted
            </span>
          </div>
        </header>

        <div className="flex-1 relative flex items-center justify-center px-4 py-12">
          <div
            className="w-full max-w-xl rounded-3xl border border-white/5 p-8 md:p-12 relative"
            style={{
              background:
                "linear-gradient(180deg, rgba(15,27,45,0.85) 0%, rgba(11,18,32,0.95) 100%)",
              boxShadow:
                "0 30px 90px -20px rgba(2,6,23,0.7), inset 0 1px 0 rgba(255,255,255,0.04)",
            }}
            data-testid="admin-login-card"
          >
            {/* Subtle inner glow border */}
            <div
              className="absolute inset-0 rounded-3xl pointer-events-none"
              style={{
                background:
                  "radial-gradient(closest-side at 50% 0%, rgba(56,189,248,0.10), transparent 60%)",
              }}
            />

            <div className="relative flex items-start gap-5">
              <LightningBolt size={86} withHalo />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-sky-200/70">
                  Management Server
                </div>
                <h1 className="mt-3 text-white font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                  Sign in to LightningFlow
                </h1>
                <p className="mt-3 text-sky-100/70 text-base md:text-lg leading-relaxed">
                  Network intelligence at the speed of{" "}
                  <span
                    className="font-semibold"
                    style={{
                      background:
                        "linear-gradient(90deg, #7DD3FC 0%, #22D3EE 60%, #38BDF8 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      textShadow: "0 0 24px rgba(34,211,238,0.25)",
                    }}
                  >
                    light.
                  </span>
                </p>
              </div>
            </div>

            {/* Divider with cyan accent */}
            <div className="relative mt-10">
              <div className="h-px w-full bg-white/5" />
              <div
                className="absolute left-0 top-0 h-px w-32"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(56,189,248,0.7) 40%, rgba(56,189,248,0) 100%)",
                }}
              />
            </div>

            <form onSubmit={submit} className="relative mt-10 space-y-7" data-testid="admin-login-form">
              <Field
                id="admin-email"
                label="Username"
                placeholder="admin"
                type="email"
                value={email}
                onChange={setEmail}
                testid="admin-login-email"
                autoComplete="username"
              />
              <Field
                id="admin-password"
                label="Password"
                placeholder="••••••••"
                type="password"
                value={password}
                onChange={setPassword}
                testid="admin-login-password"
                autoComplete="current-password"
              />

              <div>
                <label
                  htmlFor="admin-domain"
                  className="block text-white font-display text-xl font-extrabold tracking-tight mb-3"
                >
                  Domain
                </label>
                <div className="relative">
                  <select
                    id="admin-domain"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    className="w-full appearance-none rounded-xl bg-transparent border border-sky-300/30 text-white placeholder:text-sky-100/30 px-4 h-12 pr-10 focus:outline-none focus:border-sky-300 transition-colors"
                    data-testid="admin-login-domain"
                  >
                    <option value="local" className="bg-midnight-900 text-white">
                      Local user
                    </option>
                  </select>
                  <svg
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-sky-200/70"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 11.084l3.71-3.853a.75.75 0 111.08 1.04l-4.24 4.404a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>

              {error ? (
                <p className="text-sm text-red-300" data-testid="admin-login-error">
                  {error}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={submitting}
                className="w-full h-12 rounded-xl font-display font-bold text-base text-midnight-900 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 relative overflow-hidden group"
                style={{
                  background:
                    "linear-gradient(90deg, #67E8F9 0%, #22D3EE 35%, #3B82F6 100%)",
                  boxShadow:
                    "0 10px 30px -10px rgba(34,211,238,0.5), inset 0 1px 0 rgba(255,255,255,0.4)",
                }}
                data-testid="admin-login-submit"
              >
                <span
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{
                    background:
                      "linear-gradient(90deg, #38BDF8 0%, #06B6D4 35%, #2563EB 100%)",
                  }}
                />
                <span className="relative flex items-center gap-2">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {submitting ? "Signing in…" : "Sign in"}
                </span>
              </button>

              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-sky-200/40 text-center">
                Authorized personnel only · Session expires in 12h
              </p>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

function Field({ id, label, placeholder, type, value, onChange, testid, autoComplete }) {
  return (
    <div>
      <label htmlFor={id} className="block text-white font-display text-xl font-extrabold tracking-tight mb-3">
        {label}
      </label>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl bg-transparent border border-sky-300/30 text-white placeholder:text-sky-100/30 px-4 h-12 focus:outline-none focus:border-sky-300 focus:shadow-[0_0_0_3px_rgba(56,189,248,0.18)] transition-all"
        data-testid={testid}
      />
    </div>
  );
}
